import { formatMoneyOrNull } from "../../utils/money-utils";
import type { TFunction } from "i18next";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { licenceApi } from "../../services/licence.api";
import { useAppSelector } from "../../store/hooks";
import { selectSession } from "../../store/selectors/sessionSelector";
import { DoctorLicenceSummary } from "../../types/doctorLicence.definition";
import { DoctorLicencePlanType, LicenceStatus } from "../../types/enums";
import { RoleEnum } from "../../types/user.definition";
import { formatDateTime, formatTimestamp } from "../../utils/date-format-utils";
import { licenceValueLabel } from "../../utils/enum-label-utils";
import { scrollToTop } from "../../utils/scroll-utils";

/**
 * MED-1522: which statuses offer "pause".
 *
 * "Pause" and "Resume" have been wired to the BFF since the page was built,
 * but "pause" was gated on ACTIVE alone — so a licence in any other status,
 * and in particular the TRIAL a free-access grant produces, offered nothing
 * but the irreversible "Expire". Pausing is meaningful exactly where the
 * licence currently grants access, which the API's DoctorLicenceGuard defines
 * as ACTIVE or TRIAL; the other statuses (EXPIRED, CANCELLED,
 * PENDING_PAYMENT) grant none, so there is nothing to suspend.
 */
const PAUSABLE_STATUSES: string[] = [LicenceStatus.ACTIVE, LicenceStatus.TRIAL];

/**
 * MED-1587: an EXPIRED licence could only stay expired. "Reactivate" puts it
 * back in the status it had before (ACTIVE, TRIAL or PENDING_PAYMENT, decided
 * by the API from the billing facts). When the contract has genuinely ended
 * the API refuses with this code, and the admin is told to renew the licence
 * or grant free access instead.
 */
const CONTRACT_ENDED_CODE = "DOCTOR_LICENCE_CONTRACT_ENDED";

/**
 * MED-1596: a licence whose cancellation is scheduled (removeLicenseDate set)
 * but not yet effective can have that cancellation lifted. The API refuses
 * with this code when there is nothing to lift (no date, or already CANCELLED).
 */
const NOT_LIFTABLE_CODE = "DOCTOR_LICENCE_CANCELLATION_NOT_LIFTABLE";

const isCancellationLiftable = (lic: DoctorLicenceSummary): boolean =>
  !!lic.removeLicenseDate && lic.status !== LicenceStatus.CANCELLED;

type LicenceAction =
  | "expire"
  | "pause"
  | "unpause"
  | "reactivate"
  | "liftCancellation";

const ACTION_KEYS: Record<
  LicenceAction,
  { confirm: string; success: string; error: string }
> = {
  expire: {
    confirm: "doctorLicence.management.confirmExpire",
    success: "doctorLicence.management.expireSuccess",
    error: "doctorLicence.management.expireError",
  },
  pause: {
    confirm: "doctorLicence.management.confirmPause",
    success: "doctorLicence.management.pauseSuccess",
    error: "doctorLicence.management.pauseError",
  },
  unpause: {
    confirm: "doctorLicence.management.confirmUnpause",
    success: "doctorLicence.management.unpauseSuccess",
    error: "doctorLicence.management.unpauseError",
  },
  reactivate: {
    confirm: "doctorLicence.management.confirmReactivate",
    success: "doctorLicence.management.reactivateSuccess",
    error: "doctorLicence.management.reactivateError",
  },
  liftCancellation: {
    confirm: "doctorLicence.management.confirmLiftCancellation",
    success: "doctorLicence.management.liftCancellationSuccess",
    error: "doctorLicence.management.liftCancellationError",
  },
};

const STATUS_BADGE: Record<string, string> = {
  ACTIVE: "bg-green-100 text-green-700",
  PAUSED: "bg-yellow-100 text-yellow-700",
  EXPIRED: "bg-red-100 text-red-700",
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="py-3 border-b border-gray-100 last:border-0">
    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">
      {label}
    </dt>
    <dd className="text-sm text-gray-900">{value ?? "—"}</dd>
  </div>
);

/**
 * MED-1532: the detail panel printed licence amounts as bare numbers, with no
 * indication of the currency. An amount the API did not send stays undefined
 * so `DetailRow` shows its "—" rather than "F CFA 0".
 *
 * MED-1543: the design system's formatter reads the *browser's* locale, so a
 * French UI in an en-US browser rendered "F CFA 15,000" instead of
 * "15 000 F CFA". `formatMoneyOrNull` is the same Intl + XOF formatting, in
 * the UI's language.
 */
const amount = formatMoneyOrNull;

const LicenceDetailPanel: React.FC<{
  licence: DoctorLicenceSummary;
  onClose: () => void;
  t: TFunction;
}> = ({ licence, onClose, t }) => (
  <>
    <div
      className="fixed inset-0 z-40 bg-black/30"
      onClick={onClose}
      aria-hidden="true"
    />
    <div className="fixed right-0 top-0 z-50 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">
          {t("doctorLicence.management.detail.title")}
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={t("doctorLicence.management.detail.close")}
        >
          ✕
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="mb-4">
          <p className="text-lg font-semibold text-gray-900">
            {licence.doctorFirstName} {licence.doctorLastName}
          </p>
          {licence.doctorEmail && (
            <p className="text-sm text-gray-500">{licence.doctorEmail}</p>
          )}
        </div>
        <dl className="divide-y divide-gray-100">
          <DetailRow
            label={t("doctorLicence.management.plan")}
            value={licenceValueLabel(t, licence.planType)}
          />
          <DetailRow
            label={t("doctorLicence.management.status")}
            value={
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[licence.status] ?? "bg-gray-100 text-gray-600"}`}
              >
                {t(`doctorLicence.section.statuses.${licence.status}`)}
              </span>
            }
          />
          <DetailRow
            label={t("doctorLicence.management.source")}
            value={
              licence.source === "FACILITY" ? (
                <Link
                  to="/admin/facilities"
                  className="text-primary-600 hover:text-primary-800 underline"
                  onClick={onClose}
                >
                  {t("doctorLicence.management.sources.FACILITY")}
                </Link>
              ) : (
                t(
                  `doctorLicence.management.sources.${licence.source ?? "INDEPENDENT"}`
                )
              )
            }
          />
          <DetailRow
            label={t("doctorLicence.management.detail.contractDuration")}
            value={licenceValueLabel(t, licence.contractDuration)}
          />
          <DetailRow
            label={t("doctorLicence.management.detail.baseAmount")}
            value={amount(licence.baseAmount)}
          />
          <DetailRow
            label={t("doctorLicence.management.detail.monthlyAmount")}
            value={amount(licence.monthlyAmount)}
          />
          <DetailRow
            label={t("doctorLicence.management.detail.startDate")}
            value={formatDateTime(licence.startDate)}
          />
          <DetailRow
            label={t("doctorLicence.management.detail.endDate")}
            value={formatDateTime(licence.endDate)}
          />
          <DetailRow
            label={t("doctorLicence.management.detail.paymentDueDate")}
            value={formatDateTime(licence.paymentDueDate)}
          />
          <DetailRow
            label={t("doctorLicence.management.detail.lastPaymentDate")}
            value={formatDateTime(licence.lastPaymentDate)}
          />
          <DetailRow
            label={t("doctorLicence.management.detail.createdAt")}
            value={
              licence.createdAt ? formatTimestamp(licence.createdAt) : undefined
            }
          />
          <DetailRow
            label={t("doctorLicence.management.detail.updatedAt")}
            value={
              licence.updatedAt ? formatTimestamp(licence.updatedAt) : undefined
            }
          />
        </dl>
      </div>
      <div className="px-6 py-4 border-t border-gray-200">
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          {t("doctorLicence.management.detail.close")}
        </button>
      </div>
    </div>
  </>
);

export const DoctorLicencesManagement: React.FC = () => {
  const { t } = useTranslation();
  // MED-1587: the API and BFF allow expire, pause, unpause and reactivate for
  // SUPER_ADMIN only, so an ADMIN gets the list without the action buttons.
  const session = useAppSelector(selectSession);
  const isSuperAdmin = session?.user?.roleName === RoleEnum.SUPER_ADMIN;

  const [licences, setLicences] = useState<DoctorLicenceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [selectedLicence, setSelectedLicence] =
    useState<DoctorLicenceSummary | null>(null);
  const [confirming, setConfirming] = useState<{
    id: number;
    doctorId: number;
    action: LicenceAction;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [liftReason, setLiftReason] = useState("");

  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterPlan, setFilterPlan] = useState<string>("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const PAGE_SIZE = 20;

  const loadLicences = useCallback(
    (currentPage: number, status: string, plan: string) => {
      setLoading(true);
      setErrorMsg(null);
      licenceApi
        .getAllDoctorLicences({
          status: status || undefined,
          plan_type: plan || undefined,
          page: currentPage,
          size: PAGE_SIZE,
        })
        .then(data => {
          setLicences(data.content);
          setTotalPages(data.totalPages);
          setTotalElements(data.totalElements);
        })
        .catch(() => setErrorMsg(t("doctorLicence.management.errorLoading")))
        .finally(() => setLoading(false));
    },
    [t]
  );

  useEffect(() => {
    loadLicences(page, filterStatus, filterPlan);
  }, [page, filterStatus, filterPlan, loadLicences]);

  const handleFilterChange = (status: string, plan: string) => {
    setPage(0);
    setFilterStatus(status);
    setFilterPlan(plan);
  };

  const handleAction = async () => {
    if (!confirming) return;
    setActionLoading(true);
    const { action, doctorId, id } = confirming;
    try {
      if (action === "expire") {
        await licenceApi.expireDoctorLicence(doctorId, id);
      } else if (action === "pause") {
        await licenceApi.pauseDoctorLicence(doctorId, id);
      } else if (action === "unpause") {
        await licenceApi.unpauseDoctorLicence(doctorId, id);
      } else if (action === "reactivate") {
        await licenceApi.reactivateDoctorLicence(doctorId, id);
      } else {
        await licenceApi.liftDoctorLicenceCancellation(
          doctorId,
          id,
          liftReason.trim() || undefined
        );
      }
      setSuccessMsg(t(ACTION_KEYS[action].success));
      setConfirming(null);
      loadLicences(page, filterStatus, filterPlan);
    } catch (error) {
      const code = (error as { code?: unknown } | null)?.code;
      const contractEnded =
        action === "reactivate" && code === CONTRACT_ENDED_CODE;
      const notLiftable =
        action === "liftCancellation" && code === NOT_LIFTABLE_CODE;
      setErrorMsg(
        t(
          contractEnded
            ? "doctorLicence.management.reactivateContractEnded"
            : notLiftable
              ? "doctorLicence.management.liftCancellationNotLiftable"
              : ACTION_KEYS[action].error
        )
      );
    } finally {
      setActionLoading(false);
    }
  };

  const confirmMsg = confirming
    ? t(ACTION_KEYS[confirming.action].confirm)
    : "";

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select
          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          value={filterStatus}
          onChange={e => handleFilterChange(e.target.value, filterPlan)}
          aria-label={t("doctorLicence.management.filterByStatus")}
        >
          <option value="">{t("doctorLicence.management.allStatuses")}</option>
          {Object.values(LicenceStatus).map(s => (
            <option key={s} value={s}>
              {t(`doctorLicence.section.statuses.${s}`)}
            </option>
          ))}
        </select>

        <select
          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          value={filterPlan}
          onChange={e => handleFilterChange(filterStatus, e.target.value)}
          aria-label={t("doctorLicence.management.filterByPlan")}
        >
          <option value="">{t("doctorLicence.management.allPlans")}</option>
          {Object.values(DoctorLicencePlanType).map(p => (
            <option key={p} value={p}>
              {licenceValueLabel(t, p)}
            </option>
          ))}
        </select>
      </div>

      {/* Feedback messages */}
      {successMsg && (
        <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {errorMsg}
        </div>
      )}

      {/* Confirm dialog */}
      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full mx-4">
            <p className="text-sm text-gray-700 mb-5">{confirmMsg}</p>
            {confirming.action === "liftCancellation" && (
              <div className="mb-5">
                <label
                  htmlFor="lift-cancellation-reason"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t("doctorLicence.management.liftCancellationReason")}
                </label>
                <textarea
                  id="lift-cancellation-reason"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  maxLength={255}
                  value={liftReason}
                  onChange={e => setLiftReason(e.target.value)}
                  disabled={actionLoading}
                />
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirming(null)}
                disabled={actionLoading}
              >
                {t("common.cancel")}
              </button>
              <button
                className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                onClick={handleAction}
                disabled={actionLoading}
              >
                {actionLoading ? "…" : t("common.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail panel */}
      {selectedLicence && (
        <LicenceDetailPanel
          licence={selectedLicence}
          onClose={() => setSelectedLicence(null)}
          t={t}
        />
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        </div>
      ) : licences.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">
          {t("doctorLicence.management.noLicences")}
        </p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">
                    {t("doctorLicence.management.doctor")}
                  </th>
                  <th className="px-4 py-3 text-left">
                    {t("doctorLicence.management.email")}
                  </th>
                  <th className="px-4 py-3 text-left">
                    {t("doctorLicence.management.plan")}
                  </th>
                  <th className="px-4 py-3 text-left">
                    {t("doctorLicence.management.status")}
                  </th>
                  <th className="px-4 py-3 text-left">
                    {t("doctorLicence.management.source")}
                  </th>
                  <th className="px-4 py-3 text-left">
                    {t("doctorLicence.management.endDate")}
                  </th>
                  <th className="px-4 py-3 text-left">
                    {t("doctorLicence.management.paymentDueDate")}
                  </th>
                  <th className="px-4 py-3 text-left">
                    {t("doctorLicence.management.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {licences.map(lic => (
                  <tr
                    key={lic.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedLicence(lic)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {lic.doctorFirstName} {lic.doctorLastName}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {lic.doctorEmail ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {licenceValueLabel(t, lic.planType)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[lic.status] ?? "bg-gray-100 text-gray-600"}`}
                      >
                        {t(`doctorLicence.section.statuses.${lic.status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {lic.source === "FACILITY" ? (
                        <Link
                          to="/admin/facilities"
                          className="text-primary-600 hover:text-primary-800 underline text-sm"
                          onClick={e => e.stopPropagation()}
                        >
                          {t("doctorLicence.management.sources.FACILITY")}
                        </Link>
                      ) : (
                        <span className="text-gray-600">
                          {lic.source
                            ? t(
                                `doctorLicence.management.sources.${lic.source}`
                              )
                            : "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDateTime(lic.endDate)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDateTime(lic.paymentDueDate)}
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className="flex gap-2"
                        onClick={e => e.stopPropagation()}
                      >
                        {isSuperAdmin &&
                          lic.status !== LicenceStatus.EXPIRED && (
                            <button
                              className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50"
                              onClick={() =>
                                setConfirming({
                                  id: lic.id,
                                  doctorId: lic.doctorId,
                                  action: "expire",
                                })
                              }
                            >
                              {t("doctorLicence.management.expire")}
                            </button>
                          )}
                        {isSuperAdmin &&
                          PAUSABLE_STATUSES.includes(lic.status) && (
                            <button
                              className="text-xs px-2 py-1 rounded border border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                              onClick={() =>
                                setConfirming({
                                  id: lic.id,
                                  doctorId: lic.doctorId,
                                  action: "pause",
                                })
                              }
                            >
                              {t("doctorLicence.management.pause")}
                            </button>
                          )}
                        {isSuperAdmin &&
                          lic.status === LicenceStatus.PAUSED && (
                            <button
                              className="text-xs px-2 py-1 rounded border border-green-400 text-green-700 hover:bg-green-50"
                              onClick={() =>
                                setConfirming({
                                  id: lic.id,
                                  doctorId: lic.doctorId,
                                  action: "unpause",
                                })
                              }
                            >
                              {t("doctorLicence.management.unpause")}
                            </button>
                          )}
                        {isSuperAdmin &&
                          lic.status === LicenceStatus.EXPIRED && (
                            <button
                              className="text-xs px-2 py-1 rounded border border-green-400 text-green-700 hover:bg-green-50"
                              onClick={() =>
                                setConfirming({
                                  id: lic.id,
                                  doctorId: lic.doctorId,
                                  action: "reactivate",
                                })
                              }
                            >
                              {t("doctorLicence.management.reactivate")}
                            </button>
                          )}
                        {isSuperAdmin && isCancellationLiftable(lic) && (
                          <button
                            className="text-xs px-2 py-1 rounded border border-blue-400 text-blue-700 hover:bg-blue-50"
                            onClick={() => {
                              setLiftReason("");
                              setConfirming({
                                id: lic.id,
                                doctorId: lic.doctorId,
                                action: "liftCancellation",
                              });
                            }}
                          >
                            {t("doctorLicence.management.liftCancellation")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>
              {totalElements} {totalElements === 1 ? "licence" : "licences"}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={e => {
                  setPage(p => p - 1);
                  scrollToTop(e.currentTarget);
                }}
                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                ←
              </button>
              <span>
                {page + 1} / {totalPages}
              </span>
              <button
                disabled={page >= totalPages - 1}
                onClick={e => {
                  setPage(p => p + 1);
                  scrollToTop(e.currentTarget);
                }}
                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
